import React, { useState } from "react";
import cls from "./mobileSearchContainer.module.scss";
import useDebounce from "hooks/useDebounce";
import ModalContainer from "containers/modal/modal";
import { DialogProps } from "@mui/material";
import ArrowLeftLineIcon from "remixicon-react/ArrowLeftLineIcon";
import MobileSearch from "components/mobileSearch/mobileSearch";
import { useInfiniteQuery } from "react-query";
import shopService from "services/shop";
import productService from "services/product";
import SearchResult from "components/searchResult/searchResult";
import useUserLocation from "hooks/useUserLocation";
import SearchSuggestion from "components/searchSuggestion/searchSuggestion";
import { useDispatch } from "react-redux";
import { addToSearch } from "redux/slices/search";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";

const shopRoutes = ["shop/"];

// 🔹 NORMALIZZA IL TESTO DI RICERCA (apostrofi strani, spazi, ecc.)
function normalizeSearchTerm(value: string) {
  return value
    .replace(/[‘’`´]/g, "’") // tutti gli apostrofi curvi → apostrofo semplice
    .replace(/\s+/g, " ");   // spazi multipli → singolo spazio
    // NON applicare .trim() qui: verrebbe eseguito ad ogni tasto e bloccherebbe lo spazio durante la digitazione
}

export default function MobileSearchContainer(props: DialogProps) {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { pathname, query } = useRouter();
  const location = useUserLocation();
  const locale = i18n.language;
  const [searchTerm, setSearchTerm] = useState("");

  // 🔹 usa sempre il valore normalizzato per il debounce (.trim() solo qui, non durante la digitazione)
  const debouncedSearchTerm = useDebounce(
    normalizeSearchTerm(searchTerm).trim(),
    400,
  );

  const isRestaurantRoute = shopRoutes.some((item) => pathname.includes(item));

  const resetSearch = () => setSearchTerm("");

  // 🔹 wrapper: normalizza SEMPRE il valore che arriva da MobileSearch
  const handleSetSearchTerm = (value: string) => {
    setSearchTerm(normalizeSearchTerm(value));
  };

  const {
    data: shops,
    fetchNextPage: fetchShopsNextPage,
    hasNextPage: hasShopsNextPage,
    isFetchingNextPage: isFetchingShopsNextPage,
    isLoading: isShopsLoading,
  } = useInfiniteQuery(
    ["shopResult", debouncedSearchTerm, location, locale],
    ({ pageParam = 1 }) =>
      shopService.search({
        search: debouncedSearchTerm,
        page: pageParam,
        address: location,
        open: 1,
      }),
    {
      getNextPageParam: (lastPage: any) => {
        if (lastPage.meta.current_page < lastPage.meta.last_page) {
          return lastPage.meta.current_page + 1;
        }
        return undefined;
      },
      retry: false,
      enabled: !!debouncedSearchTerm && !isRestaurantRoute,
    },
  );

  const {
    data: products,
    fetchNextPage: fetchProductsNextPage,
    hasNextPage: hasProductsNextPage,
    isFetchingNextPage: isFetchingProductsNextPage,
    isLoading: isProductsLoading,
  } = useInfiniteQuery(
    ["productResult", debouncedSearchTerm, locale],
    ({ pageParam = 1 }) =>
      productService.search({
        search: debouncedSearchTerm,
        page: pageParam,
        shop_id: isRestaurantRoute ? query.id : undefined,
        address: location,
      }),
    {
      getNextPageParam: (lastPage: any) => {
        if (lastPage.meta.current_page < lastPage.meta.last_page) {
          return lastPage.meta.current_page + 1;
        }
        return undefined;
      },
      retry: false,
      enabled: !!debouncedSearchTerm,
      onSuccess: () => {
        dispatch(addToSearch(debouncedSearchTerm));
      },
    },
  );

  return (
    <ModalContainer {...props} closable={false}>
      <div className={cls.root}>
        {/* 🔹 qui usiamo il wrapper che normalizza il testo */}
        <MobileSearch
          searchTerm={searchTerm}
          setSearchTerm={handleSetSearchTerm}
        />
        <div className={cls.wrapper}>
          {!!debouncedSearchTerm && (
            <SearchResult
              isVisibleShops={!isRestaurantRoute}
              shops={shops?.pages?.flatMap((item) => item.data) || []}
              products={products?.pages?.flatMap((item) => item.data) || []}
              isLoading={isShopsLoading || isProductsLoading}
              handleClickItem={() => {
                resetSearch();
                if (props.onClose) props.onClose({}, "backdropClick");
              }}
              productTotal={products?.pages ? products.pages[0].meta.total : 0}
              shopTotal={shops?.pages ? shops.pages[0].meta.total : 0}
              isFetchingShopsNextPage={isFetchingShopsNextPage}
              isFetchingProductsNextPage={isFetchingProductsNextPage}
              hasProductsNextPage={!!hasProductsNextPage}
              hasShopsNextPage={!!hasShopsNextPage}
              fetchProductsNextPage={fetchProductsNextPage}
              fetchShopsNextPage={fetchShopsNextPage}
            />
          )}
          {!debouncedSearchTerm && (
            <SearchSuggestion setSearchTerm={handleSetSearchTerm} />
          )}
        </div>
        <div className={cls.footer}>
          <button
            className={cls.circleBtn}
            onClick={(event) => {
              if (props.onClose) props.onClose(event, "backdropClick");
            }}
          >
            <ArrowLeftLineIcon />
          </button>
        </div>
      </div>
    </ModalContainer>
  );
}
